<template lang="pug">
.new-knowledge-base()
   .inline-intro {{ $t(`${BaseTranslationPath}.header`) }}
    .search-header
      search-container(name="assets" :suggestionMode="kbSuggestionMode" :config="kbConfig" @search='onSearch' @back='onBack')
    .documents-list
      .document-item(v-for="item in kbDocuments" :key="item.id")
        .images(v-if="isImage(item)")
            .assets-container
              aiq-dialog.dialog-transparent(v-model="preview"
                                            :show-close="false"
                                            :modal-append-to-body="false"
                                            :close-on-click-modal="true")
                aiq-image(v-if="preview" :file="previewFile")
              .assets-list
                div.drag(:transfer-data="{item, type: 'asset'}",
                    :key="item.id",
                    class="asset-item",
                    @click="attachAsset(item)")
                  i.iq-ico-expand.asset-expand(@click.stop="previewAsset(item)")
                  .asset-item_img-preview
                    aiq-image(:file="getImageThumbnailPayload(item)" :isGif="isGif(item)")
                  .asset-item_title {{item.name}}
        .others(v-else)
          .document-container(@click="attachDoc(item)")
            aiq-document-icon(:mimeType="getMimeType(item)" :documentExtension="docExtension(item)" :fileId="''+item.id")
            .document-content
              .document-title {{ item.name }}
              .inner-content(v-html="$filters.sanitize(item.highlight)")
            a.url__link(@click.prevent="onHandleView(item)"
                        @click.stop
                        :href="url"
                        target="_blank") {{ $t(`${BaseTranslationPath}.view`) }}

</template>
<script>
import SearchContainer from './SearchContainer.vue';
import get from 'lodash/get';
import { mapState } from 'vuex';
import GetGlobalSource from '@/libs/mixins/GetGlobalSource';

const PAGE_SIZE = 25;
export default {
  name: 'new-knowledge-base',
  emits: ['itemClicked'],
  mixins: [GetGlobalSource],
  data() {
    return {
      BaseTranslationPath:
        'conversation_tab.side_panel.knowledge_base_accordion',
      searchKeyword: '',
      previewFile: null,
      preview: false,
      offset: 0,
      loading: false,
      hasMore: true,
      scrollContainer: null,
    };
  },
  components: {
    SearchContainer,
  },
  watch: {
    selectedConversation: {
      async handler(newVal) {
        if (newVal) {
          this.searchKeyword = '';
          await this.fetchDocuments(true);
        }
      },
      immediate: true,
      deep: true,
    },
    kbSuggestionMode: {
      async handler() {
        this.searchKeyword = '';
      },
    },
  },
  computed: {
    ...mapState({
      selectedConversation: (state) => state.conversations.selected,
      mode: (state) => state.conversations.KBMode.name,
      kbItems: (state) => state.conversations.KBMode.items,
      kbConfig: (state) => state.conversations.KBMode,
      kbType: (state) => state.conversations.KBMode.type,
      agentProfile: (state) => state.agent.profile,
      teamList: (state) => state.teams.teams,
      kbDocuments: (state) => state.conversations.kbDocuments,
      kbSuggestionMode: (state) => state.conversations.kbSuggestionMode,
    }),
    teams() {
      return this.canView('/users/filters/show_all_teams')
        ? this.teamList
        : this.agentProfile.teams;
    },
  },
  async mounted() {
    if (!this.kbSuggestionMode) {
      await this.fetchDocuments(true);
      this.scrollContainer = document.querySelector('.wrapper-children');

      if (this.scrollContainer) {
        this.scrollContainer.addEventListener('scroll', this.onScroll);
      }
    }
  },
  beforeUnmount() {
    if (this.scrollContainer) {
      this.scrollContainer.removeEventListener('scroll', this.onScroll);
    }
  },
  methods: {
    async onBack() {
      this.searchKeyword = '';
      await this.$store.dispatch('conversations/setKbSuggestions', false);
      await this.fetchDocuments(true);
      if (!this.scrollContainer) {
        this.scrollContainer = document.querySelector('.wrapper-children');
        if (this.scrollContainer) {
          this.scrollContainer.addEventListener('scroll', this.onScroll);
        }
      }
    },

    async fetchDocuments(reset = false) {
      if (this.loading) return;

      this.loading = true;

      if (reset) {
        this.offset = 0;
        this.hasMore = true;
      }

      try {
        const result = await this.$store.dispatch(
          'conversations/getKnowledgeBaseDocuments',
          [
            {
              limit: PAGE_SIZE,
              offset: this.offset,
              query: this.searchKeyword,
              teams: this.teams.map((team) => team.id),
            },
            reset,
          ],
        );
        const models = get(result, 'data.models', []);

        // If fewer than PAGE_SIZE were returned,
        // we've reached the end.
        if (models.length < PAGE_SIZE) {
          this.hasMore = false;
        } else {
          this.offset += PAGE_SIZE;
        }
      } catch (error) {
        this.$aiq.notify.error(error);
      } finally {
        this.loading = false;
      }
    },
    async onSearch(keyword) {
      this.searchKeyword = keyword;
      await this.fetchDocuments(true);
    },
    async onScroll() {
      if (this.loading || !this.hasMore) return;

      const rect = this.$el.getBoundingClientRect();
      const containerRect = this.scrollContainer.getBoundingClientRect();

      // We're within 100px of the bottom of the visible container
      const threshold = 100;
      if (rect.bottom <= containerRect.bottom + threshold) {
        this.fetchDocuments();
      }
    },
    docExtension(item) {
      if (get(item, 'meta.object_type') === 'url') {
        return 'html';
      }
      const filename = get(item, 'file.payload.name', '');
      return filename ? filename.split('.').pop() : '';
    },
    getMimeType(item) {
      return get(item, 'file.payload.mime', '');
    },
    isImage(item) {
      const type = this.getMimeType(item);
      return type.includes('image');
    },

    attachAsset(asset) {
      this.$emit('itemClicked', {
        type: 'assets',
        item: asset,
      });
    },
    attachDoc(document) {
      this.$emit('itemClicked', {
        type: 'documents',
        item: document,
      });
    },
    async onHandleView(item) {
      const objectType = get(item, 'meta.object_type');
      if (objectType === 'url' || objectType === 'text/html') {
        const url = get(item, 'meta.url');
        if (url) {
          window.open(url, '_blank');
        }
        return;
      }
      const url = await this.getFileUrl(this.getFilePayload(item));
      if (url) {
        window.open(url, '_blank');
      }
    },
    previewAsset(asset) {
      this.previewFile = this.getFilePayload(asset);
      this.preview = this.previewFile !== null;
    },
    getFilePayload(asset) {
      return get(asset, 'file.payload', null);
    },
    getImageThumbnailPayload(asset) {
      const filePayload = this.getFilePayload(asset);
      return get(filePayload, 'thumbnail', null);
    },
    isGif(asset) {
      return get(asset, 'file.payload.extension') === 'gif';
    },
  },
};
</script>

<style lang="scss" scoped>
img {
  height: 100%;
  width: 90%;
  object-fit: cover;
}
.assets-list {
  padding-left: 5px;
  padding-right: 5px;
}
.documents-list {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}
.inline-intro {
  padding-bottom: 0rem;
}
.others {
  padding-top: 10px;
  padding-bottom: 10px;
  padding-left: 5px;
  padding-right: 5px;
}
.new-knowledge-base {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 16px;
}
.search-header {
  position: sticky;
  top: 0;
  z-index: 20;
  background: #fff;
  padding-bottom: 12px;
}
.document-container {
  background-color: #fff;
  border-radius: 4px;
  box-shadow: 0 0 3px 0 #bfcbd9;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  max-height: 93px;
  justify-content: space-evenly;
  line-height: 16px;
  overflow: hidden;
  padding: 12px 0;

  .document-content {
    width: 181px;

    .document-title {
      color: #324057;
      font-family: "Museo Sans";
      font-size: 12px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .inner-content {
      color: #8492a5;
      font-size: 11px;
    }
    .url__link {
      line-height: 16px;
    }
  }
}

.wrap-items {
  display: flex;
  position: relative;
  margin-bottom: 12px;
  border-radius: 4px;
  box-shadow: 0 0 3px 0 #bfcbd9;
  background-color: #fff;
  overflow: hidden;
  cursor: pointer;
}
.el-input {
  margin: 8px 0 12px;
}

.asset-item {
  position: relative;
  margin-bottom: 12px;
  border-radius: 4px;
  box-shadow: 0 0 3px 0 rgba(191, 203, 217, 0.5);
  background-color: #fff;
  overflow: hidden;
  cursor: pointer;

  &:hover {
    box-shadow: 0 0 3px 2px rgba(191, 203, 217, 0.5);

    .asset-expand {
      opacity: 1;
    }
  }
  &:active {
    box-shadow: none;
  }

  &_img-preview {
    width: 139px;
    height: 139px;
    background-position: center center;
    background-size: cover;
  }
  &_title {
    padding: 10px;
    color: #48576a;
    font-size: 11px;
    line-height: 13px;
    font-weight: 300;
  }
}
.asset-expand {
  position: absolute;
  top: 6px;
  right: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background-color: #5993ff;
  cursor: pointer;
  opacity: 0;
}
.dialog-transparent {
  img {
    cursor: pointer;
  }
}
</style>
